import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { useMutation, gql, TypedDocumentNode, DocumentNode } from "@apollo/client";
import { NotePriorityValues, CcmsSuspension } from "@/types";
import SuspensionForm from "./SuspensionForm";
import {
  SuspensionFormValues,
  getDefaultSuspensionFormValues,
  BASE_SUSPENSION_REASON_OPTIONS,
  RESTRICTED_SUSPENSION_REASON_OPTIONS
} from "./suspensionFormConstants";
import { useCanManageSuspensionsRestricted } from "@/hooks/useCapabilities";

interface UpdateSuspensionInput {
  id: string;
  code?: string;
  startDate?: string;
  endDate?: string | null;
  note?: string | null;
  notePriority?: string | null;
}

const UPDATE_SUSPENSION_MUTATION: TypedDocumentNode<
  {
    updateSuspension: {
      success: boolean;
      message: string;
      suspension?: {
        id: string;
      };
    };
  },
  {
    input: UpdateSuspensionInput;
  }
> = gql`
  mutation UpdateSuspension($input: BackofficeUpdateSuspensionInput!) {
    updateSuspension(input: $input) {
      success
      message
      suspension {
        id
      }
    }
  }
`;

// Map suspension code from API format to form value format
const mapCodeToFormValue = (code: string): string => {
  const codeMap: Record<string, string> = {
    NPDU: "NON_PAYMENT_OF_DUES",
    GPTP: "FAILURE_TO_MEET_ACCEPTABLE_PROGRESS",
    COEV: "ETHICS_VIOLATION",
    FINE: "LATE_EMPLOYMENT_CHANGE_REPORTING",
    RTCK: "RETURN_CHECK",
    APTS: "ADVANCED_PROFESSIONAL_TRAINING_SCHOOL",
    PALY: "PLAYING_PRIVILEGES",
    BOC: "BOARD_OF_CONTROL",
    CRUN: "CREDIT_UNION_SUSPENSION",
    MSUR: "MEMBERSHIP_SURVEY_NOT_RETURNED",
    NPPN: "NON_PAYMENT_OF_PENALTY",
    QDUE: "QUESTIONNAIRE_NOT_RETURNED_NON_PAYMENT_DUES",
    QPEN: "QUESTIONNAIRE_NOT_RETURNED_NON_PAYMENT_PENALTY",
    QUES: "QUESTIONNAIRE_NOT_RETURNED",
    TRGP: "TRANS_INTO_GPTP"
  };
  return codeMap[code] || code;
};

interface EditSuspensionModalProps {
  open: boolean;
  onClose: () => void;
  suspension: CcmsSuspension;
  refetchQueries?: DocumentNode[];
  /** Number of other active suspensions (excluding the one being edited) */
  otherActiveSuspensionCount?: number;
  /** Whether the suspension being edited is currently active */
  isSelectedSuspensionActive?: boolean;
  onSuspensionMutationSuccess: () => Promise<void> | void;
}

export default function EditSuspensionModal({
  open,
  onClose,
  suspension,
  refetchQueries,
  otherActiveSuspensionCount = 0,
  isSelectedSuspensionActive = false,
  onSuspensionMutationSuccess
}: EditSuspensionModalProps) {
  const [bannerError, setBannerError] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const canManageRestrictedSuspensions = useCanManageSuspensionsRestricted();

  // Filter suspension options based on user permissions
  // Restricted suspensions (e.g., Code of Ethics) require manageSuspensionsRestricted permission
  const availableSuspensionOptions = useMemo(() => {
    const options = [...BASE_SUSPENSION_REASON_OPTIONS];
    if (canManageRestrictedSuspensions) {
      options.push(...RESTRICTED_SUSPENSION_REASON_OPTIONS);
    }
    return options;
  }, [canManageRestrictedSuspensions]);

  const editSuspensionOptions = useMemo(() => {
    const currentReasonValue = mapCodeToFormValue(suspension.code);
    const hasCurrentValue = availableSuspensionOptions.some((option) => option.value === currentReasonValue);

    if (hasCurrentValue) return availableSuspensionOptions;

    return [
      {
        value: currentReasonValue,
        label: suspension.reasonDescription || suspension.code,
        code: suspension.code
      },
      ...availableSuspensionOptions
    ];
  }, [availableSuspensionOptions, suspension.code, suspension.reasonDescription]);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { isValid, isSubmitting, isDirty }
  } = useForm<SuspensionFormValues>({
    defaultValues: getDefaultSuspensionFormValues({
      suspensionReason: mapCodeToFormValue(suspension.code),
      startDate: suspension.startDate,
      endDate: suspension.endDate || ""
    }),
    mode: "onChange"
  });

  const [updateSuspension, { loading: mutationLoading }] = useMutation(UPDATE_SUSPENSION_MUTATION, {
    refetchQueries: refetchQueries || [],
    awaitRefetchQueries: true
  });

  const handleClose = () => {
    reset();
    setBannerError("");
    onClose();
  };

  async function submit(formData: SuspensionFormValues) {
    try {
      setBannerError("");

      const originalSuspensionReason = mapCodeToFormValue(suspension.code);
      const input: UpdateSuspensionInput = {
        id: suspension.id, // Use the CCMS synthetic ID
        startDate: formData.startDate,
        endDate: formData.endDate || null
      };

      // Suspension reason is currently read-only in edit mode.
      // Only send code when a future UI allows changing it.
      if (formData.suspensionReason && formData.suspensionReason !== originalSuspensionReason) {
        input.code = formData.suspensionReason;
      }

      // Only include note if it's provided
      if (formData.note) {
        input.note = formData.note;
        input.notePriority = formData.notePriority !== NotePriorityValues.NONE ? formData.notePriority : null;
      }

      const { data } = await updateSuspension({ variables: { input } });

      if (data?.updateSuspension?.success) {
        await onSuspensionMutationSuccess();
        enqueueSnackbar(data.updateSuspension.message || "Suspension updated successfully", { variant: "default" });
        handleClose();
      } else {
        setBannerError(data?.updateSuspension?.message || "Failed to update suspension");
      }
    } catch (e) {
      const errorMessage = (e as Error).message || "An error occurred while updating suspension";
      setBannerError(`Error: ${errorMessage}`);
    }
  }

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[500px]"
      open={open}
      onClosed={handleClose}
      headline="Edit Suspension"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} testId="edit-suspension-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submit)}
            testId="edit-suspension-save-button"
            disabled={isSubmitting || !isValid || mutationLoading || !isDirty}
          >
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The suspension could not be updated." subText={bannerError} />
        </div>
      )}

      <SuspensionForm
        control={control}
        watch={watch}
        trigger={trigger}
        isEditMode={true}
        testIdPrefix="edit-suspension"
        suspensionReasonOptions={editSuspensionOptions}
        otherActiveSuspensionCount={otherActiveSuspensionCount}
        isSelectedSuspensionActive={isSelectedSuspensionActive}
      />
    </Dialog>
  );
}
